import { apiService } from './api';
import { ApplicationStatus, Pagination } from '../types/admissions';

/**
 * Wraps the /api/people, /api/families, /api/guardians, /api/cnic-cleanup
 * admin surfaces. See backend/src/controllers/peopleController.ts.
 */

// ─── Shared primitives ──────────────────────────────────────────────────────

export type PersonKind = 'applicant' | 'student' | 'alumni';
export type GuardianRelation = 'father' | 'mother' | 'guardian';
export type FamilyStatus = 'candidate' | 'resolved';

export interface Person {
  _id: string;
  schoolId: string;
  /** Given at enrollment — an applicant has none yet. */
  grNumber?: string;
  familyId: string | { _id: string; displayName?: string; status?: FamilyStatus };
  name: string;
  nameUrdu?: string;
  gender: 'male' | 'female' | 'other';
  dob?: string | null;
  cnic: string;
  passport?: string;
  phone?: string;
  email?: string;
  kind: PersonKind;
  photoFilename?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Family {
  _id: string;
  schoolId: string;
  displayName?: string;
  primaryContactGuardianId?: string | { _id: string; name: string; phone: string; relation: GuardianRelation } | null;
  feePayerGuardianId?: string | { _id: string; name: string; phone: string; relation: GuardianRelation } | null;
  status: FamilyStatus;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Guardian {
  _id: string;
  schoolId: string;
  familyId: string | { _id: string; displayName?: string };
  cnic: string;
  cnicIsPlaceholder: boolean;
  name: string;
  nameUrdu?: string;
  phone: string;
  email?: string;
  occupation?: string;
  employer?: string;
  address?: string;
  relation: GuardianRelation;
  isFeePayer: boolean;
  isEmergencyContact: boolean;
  canPickup: boolean;
  authorityDocumentFilename?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PeopleListResponse { items: Person[]; pagination: Pagination }
/** A family row in the Families list — with its children and guardians. */
export interface FamilyListItem extends Family {
  children: Array<{ _id: string; name: string; grNumber?: string; kind: PersonKind }>;
  guardians: Array<{ _id: string; name: string; relation: GuardianRelation; phone: string }>;
}
export interface FamiliesListResponse { items: FamilyListItem[]; pagination: Pagination }

/** Another family that is probably the same household. */
export interface PossibleDuplicateFamily {
  familyId: string;
  displayName: string;
  status?: FamilyStatus;
  /** "Same father's phone", "Same mother's CNIC", … */
  reasons: string[];
  children: Array<{ name: string; grNumber?: string }>;
}

/** A child's admission application, as listed on the family. */
export interface FamilyApplication {
  _id: string;
  personId: string;
  name: string;
  status: ApplicationStatus;
  cycleId: { _id: string; name: string } | string;
  classId: { _id: string; name: string } | string;
  createdAt: string;
}

/** An applicant's brother or sister, and where they are at the school now. */
export interface Sibling extends Pick<Person, '_id' | 'name' | 'kind' | 'grNumber' | 'photoFilename'> {
  /** Current class, for an enrolled sibling. */
  enrollment: { classId: { _id: string; name: string }; sectionInstanceId?: { _id: string; name: string } | null } | null;
  /** Latest application, for a sibling who is still applying. */
  application: (Pick<FamilyApplication, '_id' | 'status' | 'cycleId' | 'classId'> & { applicationNumber: string }) | null;
}

/** How an application's household was found: its linked `family`, or a
 *  parent's CNIC (`guardian_cnic`) before the family is linked. */
export interface ApplicationSiblings {
  basis: 'family' | 'guardian_cnic' | 'none';
  family: Pick<Family, '_id' | 'displayName' | 'status'> | null;
  matchedGuardians: Array<{ name: string; relation: GuardianRelation }>;
  siblings: Sibling[];
}

export interface PersonDetailResponse {
  person: Person;
  guardians: Guardian[];
  student?: {
    _id: string;
    grNumber: string;
    batchId?: string | { _id: string; name: string };
    rollNumber?: string;
    isActive: boolean;
  } | null;
}

export interface FamilyDetailResponse {
  family: Family;
  people: Person[];
  guardians: Guardian[];
  applications: FamilyApplication[];
  possibleDuplicates: PossibleDuplicateFamily[];
}

/** A parent already at the school, found by CNIC — with their family. */
export interface GuardianLookup {
  guardianId: string;
  family: Pick<Family, '_id' | 'displayName' | 'status'>;
  children: Array<{ _id: string; name: string; grNumber?: string; kind: PersonKind }>;
  guardians: Array<Pick<Guardian, '_id' | 'name' | 'relation' | 'cnic' | 'phone' | 'email' | 'occupation'>>;
}

export interface CnicCleanupStatus {
  studentsMissingCnic: number;
  guardiansWithPlaceholderCnic: number;
}

// ─── People ─────────────────────────────────────────────────────────────────

export const peopleService = {
  listPeople: async (params?: {
    q?: string;
    kind?: PersonKind;
    familyId?: string;
    page?: number;
    limit?: number;
  }): Promise<PeopleListResponse> => {
    const r = await apiService.get<PeopleListResponse>('/people', params);
    return r.data!;
  },

  getPerson: async (id: string): Promise<PersonDetailResponse> => {
    const r = await apiService.get<PersonDetailResponse>(`/people/${id}`);
    return r.data!;
  },

  updatePerson: async (id: string, payload: Partial<Person>): Promise<Person> => {
    const r = await apiService.patch<Person>(`/people/${id}`, payload);
    return r.data!;
  },

  // ─── Families ────────────────────────────────────────────────────────────
  listFamilies: async (params?: {
    q?: string;
    status?: FamilyStatus;
    /** Only families with two or more children. */
    siblings?: boolean;
    page?: number;
    limit?: number;
  }): Promise<FamiliesListResponse> => {
    const r = await apiService.get<FamiliesListResponse>('/families', params);
    return r.data!;
  },

  getFamily: async (id: string): Promise<FamilyDetailResponse> => {
    const r = await apiService.get<FamilyDetailResponse>(`/families/${id}`);
    return r.data!;
  },

  createFamily: async (payload: { displayName?: string }): Promise<Family> => {
    const r = await apiService.post<Family>('/families', payload);
    return r.data!;
  },

  updateFamily: async (id: string, payload: Partial<Family>): Promise<Family> => {
    const r = await apiService.patch<Family>(`/families/${id}`, payload);
    return r.data!;
  },

  attachPersonToFamily: async (familyId: string, personId: string): Promise<Person> => {
    const r = await apiService.post<Person>(`/families/${familyId}/attach-person`, { personId });
    return r.data!;
  },

  addGuardianToFamily: async (familyId: string, payload: Partial<Guardian> & {
    cnic: string; name: string; phone: string; relation: GuardianRelation;
  }): Promise<Guardian> => {
    const r = await apiService.post<Guardian>(`/families/${familyId}/guardians`, payload);
    return r.data!;
  },

  mergeFamilies: async (payload: {
    sourceFamilyId: string;
    targetFamilyId: string;
    keepGuardians?: 'source' | 'target' | 'both_dedupe_by_cnic';
  }): Promise<{ movedPersons: number; movedGuardians: number }> => {
    const r = await apiService.post<{ movedPersons: number; movedGuardians: number }>('/families/merge', payload);
    return r.data!;
  },

  // ─── Guardians ───────────────────────────────────────────────────────────
  /** Is this CNIC already a parent at the school? `null` when it isn't. */
  lookupGuardian: async (cnic: string): Promise<GuardianLookup | null> => {
    const r = await apiService.get<GuardianLookup | null>('/guardians/lookup', { cnic });
    return r.data ?? null;
  },

  updateGuardian: async (id: string, payload: Partial<Guardian>): Promise<Guardian> => {
    const r = await apiService.patch<Guardian>(`/guardians/${id}`, payload);
    return r.data!;
  },

  deleteGuardian: async (id: string): Promise<Guardian> => {
    const r = await apiService.delete<Guardian>(`/guardians/${id}`);
    return r.data!;
  },

  // ─── CNIC cleanup ────────────────────────────────────────────────────────
  cnicCleanupStatus: async (): Promise<CnicCleanupStatus> => {
    const r = await apiService.get<CnicCleanupStatus>('/cnic-cleanup/status');
    return r.data!;
  },
};
